// frontend/src/pages/auth/Signup.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Collapse
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutlined,
  EmailOutlined,
  LockOutlined,
  BusinessOutlined,
  CheckCircleOutlined,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import PasswordStrengthMeter from '../../components/common/PasswordStrengthMeter';

const schema = yup.object({
  firstName: yup
    .string()
    .required('First name is required')
    .min(1, 'First name is required')
    .max(50, 'First name is too long'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(1, 'Last name is required')
    .max(50, 'Last name is too long'),
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  organizationName: yup
    .string()
    .max(100, 'Organization name is too long')
});

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showOrgField, setShowOrgField] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      organizationName: ''
    }
  });

  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  const watchedFirstName = watch('firstName');

  // Debounced email availability check
  useEffect(() => {
    const checkEmail = async () => {
      if (watchedEmail && watchedEmail.includes('@') && watchedEmail.includes('.')) {
        setEmailChecking(true);
        try {
          const response = await authAPI.checkEmailAvailability(watchedEmail);
          setEmailAvailable(response.data.available);
        } catch (error) {
          setEmailAvailable(null);
        }
        setEmailChecking(false);
      } else {
        setEmailAvailable(null);
      }
    };

    const timeoutId = setTimeout(checkEmail, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedEmail]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');

    try {
      const signupData = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
        organizationName: data.organizationName?.trim() || undefined,
        country: 'VN' // Default to Vietnam
      };

      const response = await authAPI.signup(signupData);

      if (response.data.success) {
        setSuccess(true);
        toast.success('Account created! Please check your email to verify.');

        // Store tokens for immediate access
        if (response.data.data.token) {
          localStorage.setItem('token', response.data.data.token);
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
          localStorage.setItem('user', JSON.stringify(response.data.data.user));

          // Set organization context
          if (response.data.data.organization) {
            localStorage.setItem('currentOrganizationId', response.data.data.organization.id);
          }
        }

        // Redirect to verification pending page or dashboard
        setTimeout(() => {
          navigate('/verify-email-sent', {
            state: {
              email: data.email,
              organizationName: response.data.data.organization?.name
            }
          });
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Signup failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <CheckCircleOutlined sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Account Created!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please check your email to verify your account.
          </Typography>
          <CircularProgress sx={{ mt: 3 }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full max-w-md"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-1" style={{ width: '280px', height: '80px' }}>
            <img
              src="/Lingo24.svg"
              alt="MTX"
              className="w-full h-full object-contain"
            />
          </div>
          <Typography variant="h5" className="font-bold text-gray-900">
            Create Your Account
          </Typography>
          <Typography variant="body2" className="text-gray-600 mt-1">
            Start translating in minutes with a free account
          </Typography>
        </motion.div>

        {/* Form */}
        <motion.div variants={itemVariants}>
          <Paper elevation={0} className="p-6 border border-gray-200">
            {error && (
              <Alert severity="error" className="mb-4" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Name Fields - Side by Side */}
              <Box display="flex" gap={2}>
                <TextField
                  {...register('firstName')}
                  label="First Name"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  autoComplete="given-name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlined className="text-gray-400" />
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  {...register('lastName')}
                  label="Last Name"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  autoComplete="family-name"
                />
              </Box>

              {/* Email */}
              <TextField
                {...register('email')}
                label="Email Address"
                type="email"
                fullWidth
                error={!!errors.email || emailAvailable === false}
                helperText={
                  errors.email?.message ||
                  (emailAvailable === false && 'This email is already registered')
                }
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined className="text-gray-400" />
                    </InputAdornment>
                  ),
                  endAdornment: emailChecking ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : emailAvailable === true ? (
                    <InputAdornment position="end">
                      <CheckCircleOutlined color="success" />
                    </InputAdornment>
                  ) : null
                }}
              />

              {/* Password */}
              <Box>
                <TextField
                  {...register('password')}
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined className="text-gray-400" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <PasswordStrengthMeter password={watchedPassword} />
              </Box>

              {/* Confirm Password */}
              <TextField
                {...register('confirmPassword')}
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined className="text-gray-400" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {/* Optional Organization Name */}
              <Box>
                <Button
                  size="small"
                  onClick={() => setShowOrgField(!showOrgField)}
                  endIcon={showOrgField ? <ExpandLess /> : <ExpandMore />}
                  sx={{ textTransform: 'none', color: 'text.secondary', mb: 1 }}
                >
                  {showOrgField ? 'Hide' : 'Add'} workspace name (optional)
                </Button>
                <Collapse in={showOrgField}>
                  <TextField
                    {...register('organizationName')}
                    label="Workspace Name"
                    fullWidth
                    placeholder={watchedFirstName ? `${watchedFirstName}'s Workspace` : 'My Workspace'}
                    error={!!errors.organizationName}
                    helperText={errors.organizationName?.message || 'Leave empty for auto-generated name'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessOutlined className="text-gray-400" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Collapse>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Free Tier Benefits */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Free Plan Includes:
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary">
                  3,000 MT words/month &bull; 3 projects &bull; 5,000 TM entries &bull; Office docs support
                </Typography>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || emailAvailable === false}
                sx={{ py: 1.5 }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Creating Account...
                  </>
                ) : (
                  'Create Free Account'
                )}
              </Button>

              {/* Terms */}
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                By signing up, you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline">
                  Privacy Policy
                </Link>
              </Typography>
            </form>
          </Paper>
        </motion.div>

        {/* Login Link */}
        <motion.div variants={itemVariants} className="mt-4 text-center text-sm text-gray-600">
          <span>Already have an account?</span>{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </motion.div>

        {/* Footer */}
        <motion.div variants={itemVariants} className="mt-6 text-center text-xs text-gray-500">
          <div><strong>MTX</strong> powered by <strong>DLX Technology</strong></div>
          <div>&copy;{new Date().getFullYear()} DLX Technology Co., Ltd</div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Signup;
